import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container, 
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';
import logoImage from '../assets/logo.png';
import { t } from '../utils/textUtils';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    // Validasi token (opsional)
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        return;
      }
      
      // Anda bisa menambahkan endpoint untuk memvalidasi token jika diperlukan
      // Untuk sementara, kita anggap token valid
    };
    
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validasi password
    if (!newPassword) {
      toast.error(t('SILAKAN MASUKKAN PASSWORD BARU'));
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(t('PASSWORD HARUS MINIMAL 6 KARAKTER'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(t('PASSWORD DAN KONFIRMASI PASSWORD TIDAK COCOK'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword
      });
      
      setResetSuccess(true);
      toast.success(response.data.message || t('PASSWORD BERHASIL DIUBAH'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('GAGAL MERESET PASSWORD'));
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (!tokenValid) {
    return (
      <Container component="main" maxWidth="xs">
        <Box 
          sx={{ 
            mt: 8, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
          }}
        >
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <img src={logoImage} alt="Hakiki Steel Logo" style={{ maxWidth: '100%', height: 'auto', marginBottom: '10px' }} />
          </Box>
          
          <Typography component="h1" variant="h3" sx={{ mb: 4, fontWeight: 500, textAlign: 'center' }}>
            {t('LINK TIDAK VALID')}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
            {t('LINK RESET PASSWORD TIDAK VALID ATAU SUDAH KEDALUWARSA.')}
          </Typography>
          
          <Button
            fullWidth
            variant="contained"
            onClick={handleBackToLogin}
            sx={{ 
              mt: 2, 
              py: 1.5,
              backgroundColor: '#F0C14B',
              '&:hover': {
                backgroundColor: '#DBA800',
              }
            }}
          >
            {t('KEMBALI KE LOGIN')}
          </Button>
        </Box>
      </Container>
    );
  }

  if (resetSuccess) {
    return (
      <Container component="main" maxWidth="xs">
        <Box 
          sx={{ 
            mt: 8, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
          }}
        >
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <img src={logoImage} alt="Hakiki Steel Logo" style={{ maxWidth: '100%', height: 'auto', marginBottom: '10px' }} />
          </Box>
          
          <Typography component="h1" variant="h3" sx={{ mb: 4, fontWeight: 500, textAlign: 'center' }}>
            {t('PASSWORD DIUBAH')}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
            {t('PASSWORD ANDA TELAH BERHASIL DIUBAH. SILAKAN LOGIN DENGAN PASSWORD BARU ANDA.')}
          </Typography>
          
          <Button
            fullWidth
            variant="contained"
            onClick={handleBackToLogin}
            sx={{ 
              mt: 2, 
              py: 1.5,
              backgroundColor: '#F0C14B',
              '&:hover': {
                backgroundColor: '#DBA800',
              }
            }}
          >
            {t('LOGIN')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box 
        sx={{ 
          mt: 8, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <img src={logoImage} alt="Hakiki Steel Logo" style={{ maxWidth: '100%', height: 'auto', marginBottom: '10px' }} />
        </Box>
        
        <Typography component="h1" variant="h3" sx={{ mb: 4, fontWeight: 500, textAlign: 'center' }}>
          {t('RESET PASSWORD')}
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('MASUKKAN PASSWORD BARU ANDA.')}
          </Typography>
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword"
            label={t('PASSWORD BARU')}
            type={showPassword ? 'text' : 'password'}
            id="newPassword"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label={t('KONFIRMASI PASSWORD')}
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirmPassword"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ 
              mt: 3, 
              mb: 2,
              py: 1.5,
              backgroundColor: '#F0C14B',
              '&:hover': {
                backgroundColor: '#DBA800',
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : t('RESET PASSWORD')}
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            onClick={handleBackToLogin}
            sx={{ 
              py: 1.5,
              borderColor: '#F0C14B',
              color: '#5D4037',
              '&:hover': {
                borderColor: '#DBA800',
                backgroundColor: 'rgba(240, 193, 75, 0.04)',
              }
            }}
          >
            {t('KEMBALI KE LOGIN')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default ResetPassword;